import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { orderApi } from '../../lib/supabase-api';
import { Order } from '../../types';
import { useToast } from '../ui/Toast';

const SUPPORT_EMAIL = 'support@liberacure.com';

const ContactMethod: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { orderId } = useParams<{ orderId: string }>();
  const { isLoggedIn, user } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // NOTE: cart is intentionally NOT cleared on this page mount. The previous
  // unconditional clear violated the standard e-commerce rule that cart only
  // empties after payment confirms — bank-transfer users land here BEFORE
  // sending money, so clearing here would erase a cart they may need to
  // re-checkout if the transfer never happens.
  // Cart clear is now handled by DB trigger trg_clear_cart_on_paid which fires
  // on orders.payment_status='paid' (admin marks bank transfers as paid after
  // confirming the wire) — covers all payment methods uniformly.

  useEffect(() => {
    if (!isLoggedIn) { navigate('/login'); return; }
    const loadOrderInfo = async () => {
      if (!orderId) { setIsLoading(false); return; }
      try {
        const foundOrder = await orderApi.getById(orderId);
        if (foundOrder) { setOrder(foundOrder); }
        else { toast.error('Order not found.'); navigate('/'); }
      } catch (error) { toast.error('Failed to load order information.'); navigate('/'); }
      finally { setIsLoading(false); }
    };
    loadOrderInfo();
  }, [isLoggedIn, navigate, orderId]);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy. Please copy the address manually.');
    }
  };

  if (!isLoggedIn) return null;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-warm-200 border-t-forest-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-warm-500 mb-4">Order not found.</p>
          <Link to="/" className="text-gold-400 hover:text-forest-800 font-medium text-sm">Return to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-100">
      {/* Success Banner */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 text-center">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="font-display text-display-sm text-white mb-2">Order Submitted!</h1>
          <p className="text-green-100">If anything's unclear, email us at the address below.</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 -mt-6">
        {/* Order Summary Card */}
        <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-warm-200">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-forest-900">Order Summary</h2>
              <span className="text-xs font-mono text-warm-500">{order.orderNumber}</span>
            </div>
          </div>
          <div className="px-6 py-4 space-y-2.5">
            <div className="flex justify-between text-sm">
              <span className="text-warm-500">Subtotal</span>
              <span className="text-forest-900">${order.items.reduce((sum: number, item: any) => sum + item.price, 0).toLocaleString()}</span>
            </div>
            {(order.discountAmount || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-warm-500">Discount</span>
                <span className="text-gold-400">-${(order.discountAmount || 0).toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-warm-500">Shipping</span>
              <span className="text-forest-900">{(order.shippingFee || 0) > 0 ? `$${(order.shippingFee || 0).toLocaleString()}` : 'Free'}</span>
            </div>
            <div className="flex justify-between pt-2.5 border-t border-warm-200">
              <span className="text-sm font-semibold text-forest-900">Total</span>
              <span className="text-lg font-bold text-gold-400">${order.totalAmount.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Support email — plain text, no mailto so it never opens Outlook etc. */}
        <h3 className="text-sm font-semibold text-forest-900 mb-3 px-1">Need help?</h3>
        <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-cream-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-warm-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-base font-semibold text-forest-900 mb-1">Email support</h4>
              <p className="text-sm text-warm-500 mb-3">We reply within 24 hours.</p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="font-mono text-sm text-forest-900 bg-cream-100 border border-warm-200 rounded-lg px-3 py-2 select-all break-all">
                  {SUPPORT_EMAIL}
                </span>
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-forest-700 hover:text-forest-900 px-3 py-2 rounded-lg border border-warm-200 hover:border-warm-300 hover:bg-cream-100 transition-all"
                >
                  {copied ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Copied
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="bg-forest-50 border border-forest-100 rounded-xl p-4 mb-8">
          <p className="text-sm text-forest-800 text-center">
            Please include your order number <strong className="font-semibold">{order.orderNumber}</strong> when contacting us.
          </p>
        </div>

        {/* Return */}
        <div className="text-center pb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-warm-500 hover:text-forest-700 font-medium transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Return to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ContactMethod;
